type Mark = { type: string; attrs?: Record<string, unknown> }
type Node = { type: string; attrs?: Record<string, unknown>; content?: Node[]; text?: string; marks?: Mark[] }

function textNode(text: string, marks?: Mark[]): Node {
  return marks?.length ? { type: 'text', text, marks } : { type: 'text', text }
}

function inline(value: string): Node[] {
  const out: Node[] = []
  const re = /(\*\*[^*]+\*\*|__[^_]+__|~~[^~]+~~|\*[^*]+\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^)]+\))/g
  let last = 0
  let match: RegExpExecArray | null

  while ((match = re.exec(value))) {
    if (match.index > last) out.push(textNode(value.slice(last, match.index)))
    const token = match[0]
    if (token.startsWith('**') || token.startsWith('__')) out.push(textNode(token.slice(2, -2), [{ type: 'bold' }]))
    else if (token.startsWith('~~')) out.push(textNode(token.slice(2, -2), [{ type: 'strike' }]))
    else if (token.startsWith('*') || token.startsWith('_')) out.push(textNode(token.slice(1, -1), [{ type: 'italic' }]))
    else if (token.startsWith('`')) out.push(textNode(token.slice(1, -1), [{ type: 'code' }]))
    else {
      const link = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
      if (link) out.push(textNode(link[1], [{ type: 'link', attrs: { href: link[2], target: '_blank', rel: 'noopener noreferrer', class: null } }]))
    }
    last = match.index + token.length
  }
  if (last < value.length) out.push(textNode(value.slice(last)))
  return out.length ? out : [textNode(value)]
}

function paragraph(text: string): Node {
  return { type: 'paragraph', content: inline(text) }
}

const listMatch = (line: string) => line.match(/^(\s*)([-*+]|\d+[.)])\s+(.+)$/)

function parseList(lines: string[], start: number, baseIndent: number): [Node, number] {
  const first = listMatch(lines[start])!
  const ordered = /^\d/.test(first[2])
  const items: Node[] = []
  let i = start

  while (i < lines.length) {
    const match = listMatch(lines[i])
    if (!match) break
    const indent = match[1].replace(/\t/g, '    ').length
    if (indent < baseIndent) break
    if (indent > baseIndent) {
      if (!items.length) break
      const [nested, next] = parseList(lines, i, indent)
      items[items.length - 1].content!.push(nested)
      i = next
      continue
    }
    if (/^\d/.test(match[2]) !== ordered) break
    items.push({ type: 'listItem', content: [paragraph(match[3])] })
    i++
  }

  const attrs = ordered ? { start: Number(first[2].match(/^\d+/)?.[0] || 1) } : undefined
  return [{ type: ordered ? 'orderedList' : 'bulletList', ...(attrs ? { attrs } : {}), content: items }, i]
}

function splitRow(line: string): string[] {
  let value = line.trim()
  if (value.startsWith('|')) value = value.slice(1)
  if (value.endsWith('|')) value = value.slice(0, -1)
  return value.split(/(?<!\\)\|/).map(cell => cell.trim().replace(/\\\|/g, '|'))
}

function isDivider(line: string): boolean {
  const cells = splitRow(line)
  return cells.length > 0 && cells.every(cell => /^:?-{3,}:?$/.test(cell))
}

function parseTable(lines: string[], start: number): [Node, number] | null {
  if (start + 1 >= lines.length || !lines[start].includes('|') || !isDivider(lines[start + 1])) return null
  const rows: string[][] = [splitRow(lines[start])]
  let i = start + 2
  while (i < lines.length && lines[i].includes('|') && lines[i].trim()) rows.push(splitRow(lines[i++]))
  const width = Math.max(...rows.map(row => row.length))
  const makeCell = (text: string, header: boolean): Node => ({
    type: header ? 'tableHeader' : 'tableCell',
    attrs: { colspan: 1, rowspan: 1, colwidth: null },
    content: [paragraph(text)],
  })
  const tableRows: Node[] = rows.map((row, rowIndex) => ({
    type: 'tableRow',
    content: Array.from({ length: width }, (_, cellIndex) => makeCell(row[cellIndex] || '', rowIndex === 0)),
  }))
  return [{ type: 'table', content: tableRows }, i]
}

export function markdownToQirtaas(markdown: string): Record<string, unknown> {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n')
  const content: Node[] = []
  let i = 0

  while (i < lines.length) {
    const line = lines[i].trimEnd()
    if (!line.trim()) { i++; continue }

    const parsedTable = parseTable(lines, i)
    if (parsedTable) { content.push(parsedTable[0]); i = parsedTable[1]; continue }

    const heading = line.match(/^(#{1,6})\s+(.+)$/)
    if (heading) { content.push({ type: 'heading', attrs: { level: heading[1].length }, content: inline(heading[2]) }); i++; continue }

    const list = listMatch(line)
    if (list) {
      const indent = list[1].replace(/\t/g, '    ').length
      const [node, next] = parseList(lines, i, indent)
      content.push(node); i = next; continue
    }

    if (/^>\s?/.test(line)) {
      const rows: string[] = []
      while (i < lines.length && /^>\s?/.test(lines[i])) rows.push(lines[i++].replace(/^>\s?/, ''))
      content.push({ type: 'blockquote', content: rows.map(paragraph) }); continue
    }

    if (/^```/.test(line)) {
      const language = line.slice(3).trim() || null
      const rows: string[] = []
      i++
      while (i < lines.length && !/^```/.test(lines[i])) rows.push(lines[i++])
      if (i < lines.length) i++
      content.push({ type: 'codeBlock', attrs: { language }, content: rows.length ? [textNode(rows.join('\n'))] : [] }); continue
    }

    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) { content.push({ type: 'horizontalRule' }); i++; continue }

    const rows = [line.trim()]
    i++
    while (i < lines.length && lines[i].trim()) {
      if (/^(#{1,6})\s+/.test(lines[i]) || listMatch(lines[i]) || /^>\s?/.test(lines[i]) || /^```/.test(lines[i]) || parseTable(lines, i)) break
      rows.push(lines[i++].trim())
    }
    content.push(paragraph(rows.join(' ')))
  }

  return { type: 'doc', content: content.length ? content : [{ type: 'paragraph' }] }
}
